/**
 * Utility for exporting candidates with selected columns to Excel
 * Requires: xlsx library to be installed (npm install xlsx)
 */

import moment from "moment";

/**
 * Convert candidate data to Excel format with selected columns
 * @param {Array} data - Array of candidate objects
 * @param {Array} selectedColumns - Array of column keys to export
 * @param {Array} columnDefinitions - Array of column definitions with key and label
 * @returns {Array} - Formatted data for Excel
 */
export const formatCandidatesForExcel = (data, selectedColumns, columnDefinitions) => {
  if (!data || data.length === 0) return [];

  // Create a map of column keys to labels
  const columnMap = {};
  columnDefinitions.forEach((col) => {
    columnMap[col.key] = col.label;
  });

  // Format the data based on selected columns
  return data.map((candidate) => {
    const row = {};
    selectedColumns.forEach((columnKey) => {
      const label = columnMap[columnKey];
      
      // Handle different data types and nested properties
      switch (columnKey) {
        case "name":
          row[label] = candidate.candidateName || candidate.name || "";
          break;
        case "mobileNumber":
          row[label] =
            candidate.candidatePhone ||
            candidate.mobileNumber ||
            candidate.mobile_number ||
            "";
          break;
        case "emailId":
          row[label] = candidate.email || candidate.emailId || candidate.email_id || "";
          break;
        case "role":
          row[label] = candidate.role || candidate.roleName || candidate.role_name || "";
          break;
        case "currentCompany":
          row[label] =
            candidate.currentCompany ||
            candidate.current_company ||
            candidate.currentOrganisation ||
            candidate.currentOrganization ||
            "";
          break;
        case "totalExperienceMonths":
          row[label] =
            candidate.totalExperienceMonths ??
            candidate.total_experience_months ??
            candidate.totalExperienceInMonths ??
            "";
          break;
        case "location":
          row[label] = candidate.location || candidate.currentLocation || "";
          break;
        case "currentCTC":
          row[label] =
            candidate.currentCTC ??
            candidate.currentCtc ??
            candidate.current_ctc ??
            "";
          break;
        case "expectedCTC":
          row[label] =
            candidate.expectedCTC ??
            candidate.expectedCtc ??
            candidate.expected_ctc ??
            "";
          break;
        case "noticePeriod":
          row[label] =
            candidate.noticePeriod ??
            candidate.notice_period ??
            candidate.noticePeriodDays ??
            "";
          break;
        case "highestQualification":
          row[label] =
            candidate.highestQualification ||
            candidate.highest_qualification ||
            candidate.qualification ||
            "";
          break;
        // Legacy fields support
        case "id":
          row[label] = candidate.id || "";
          break;
        default:
          row[label] = candidate[columnKey] || "";
      }
    });
    return row;
  });
};

/**
 * Download candidates as Excel using XLSX
 * @param {Array} data - Array of candidate objects
 * @param {Array} selectedColumns - Array of column keys to export
 * @param {Array} columnDefinitions - Array of column definitions
 * @param {String} fileName - Name of the excel file (without extension)
 */
export const downloadCandidatesExcel = (
  data,
  selectedColumns,
  columnDefinitions,
  fileName = "candidates"
) => {
  try {
    // Check if xlsx library is available
    const XLSX = require("xlsx");
    
    const formattedData = formatCandidatesForExcel(data, selectedColumns, columnDefinitions);
    
    // Create a new workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates");
    
    // Auto-fit column widths (SheetJS expects objects like `{ wch: 20 }`)
    // Compute width from header + values (clamped to keep Excel readable)
    const headers = Object.keys(formattedData[0] || {});
    const maxScanRows = 50;
    ws["!cols"] = headers.map((header) => {
      let maxLen = String(header || "").length;
      for (let i = 0; i < Math.min(formattedData.length, maxScanRows); i++) {
        const val = formattedData[i]?.[header];
        const len = val == null ? 0 : String(val).length;
        if (len > maxLen) maxLen = len;
      }
      const wch = Math.max(12, Math.min(60, maxLen + 2));
      return { wch, hidden: false };
    });
    
    // Write the file
    XLSX.writeFile(wb, `${fileName}.xlsx`);
    
    return true;
  } catch (error) {
    console.error("Error downloading Excel file:", error);
    return false;
  }
};
